/**
 * TypeScript interfaces extracted from the legacy entity classes in MyScripts/Entities/.
 * These represent the shapes returned by the backend API and used in the Promise wrappers.
 *
 * NOTE: These are intentionally kept loose (many `any` fields) to match the current
 * untyped legacy code. They can be tightened incrementally as screens are migrated.
 */

// ── Shared / small types ──────────────────────────────────────────────

export interface PhoneNumberData {
  phoneType: string;
  phoneNumber: string;
}

export interface EmailData {
  id: number | null;
  email: string | null;
  emailType: string | null;
}

export interface AttachmentData {
  id: number | null;
  signedURL: string;
  filename: string;
  title: string;
  typeName: string | null;
  createdBy: string;
  dateCreated: string;
}

export interface AttachmentInfoData {
  id: number | null;
  activationKey: string;
}

export interface WarningData {
  id: number;
  priority: number;
  warning: string;
}

export interface GateCodeData {
  id: number;
  gateCode: string;
}

export interface ConnectionsData {
  people: any[];
  vehicles: any[];
  clients: any[];
  businesses: any[];
  reports: any[];
}

export interface HeightData {
  displayUnits: string;
  mm: number;
}

export interface WeightData {
  displayUnits: string;
  grams: number;
}

// ── Officer ───────────────────────────────────────────────────────────

export interface OfficerData {
  id: number;
  officer: number;
  officerName: string;
  dispatched: string | null;
  whenDispatchedChanged: string | null;
  enroute: string | null;
  whenEnrouteChanged: string | null;
  onScene: string | null;
  whenOnSceneChanged: string | null;
  cleared: string | null;
  whenClearedChanged: string | null;
  isPrimary: boolean;
  startingMileage: number;
  endingMileage: number;
  eventId: string | null;
  callCardId: number | null;
  isQueued: boolean | null;
}

// ── CCPerson / CCVehicle (call card sub-entities) ─────────────────────

export interface CCPersonData {
  id: number | null;
  firstName: string | null;
  lastName: string | null;
  dob: string | null;
  license: string | null;
  notes: string | null;
  npsdPerson: number | null;
}

export interface CCVehicleData {
  id: number | null;
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  plateState: string | null;
  plateNumber: string | null;
  vehicleType: any;
  notes: string | null;
  npsdVehicle: number | null;
}

// ── Call Card ─────────────────────────────────────────────────────────

export interface CallCardClientGroupSiteData {
  id: number;
  siteName: string;
}

export interface CallCardClientGroupData {
  id: number;
  name: string;
  sites: number[];
  allSites: CallCardClientGroupSiteData[];
}

export interface CallCardData {
  id: number;
  prevCallCardId: number;
  createdAt: string | null;
  eventId: string;
  relatedCardsId: number;
  company: number;
  callDateTime: string | null;
  callLocName: string;
  callLocAddress: string;
  callLocAddress2: string;
  callLocCity: string;
  callLocState: string;
  callLocZip: string;
  callLocCountry: string;
  callLocFormattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  /** Learned-address row the pin came from; kept by the server only for an unadjusted HIGH answer. */
  learnedAddressId?: number | null;
  customLocationId: boolean | null;
  callerName: string;
  callerPhone: string;
  callerLocation: string;
  callDisposition: number;
  callDispositionValue: string;
  dispositionNotes: string;
  primaryOfficer: number;
  primaryOfficerName: string;
  primaryDispatched: string | null;
  whenPrimaryDispatchedChanged: string | null;
  primaryEnroute: string | null;
  whenPrimaryEnrouteChanged: string | null;
  primaryOnScene: string | null;
  whenPrimaryOnSceneChanged: string | null;
  primaryCleared: string | null;
  whenPrimaryClearedChanged: string | null;
  ccStatus: number;
  author: number;
  priority: number;
  primaryStartingMileage: number;
  primaryEndingMileage: number;
  natureOfCall: number[];
  natureOfCallValues: string[];
  backupUnits: OfficerData[];
  queuedUnits: OfficerData[];
  people: CCPersonData[];
  vehicles: CCVehicleData[];
  attachmentList: AttachmentData[];
  attachmentInfoList: AttachmentInfoData[];
  callCommentsToAttach: number[];
  client: CallCardClientGroupData;
  citationCount: number;
  updateAndReclose: boolean;
}

export interface CallCommentData {
  id: number;
  dateCreated: string;
  creatorName: string;
  entry: string;
}

// ── Nature of Call ────────────────────────────────────────────────────

export interface NatureOfCallData {
  id: number;
  code: number;
  value: string;
  gCode: number | null;
  gValue: string | null;
  sgCode: number | null;
  sgValue: string | null;
  sop: string | null;
  listIdx: number;
  isEnabled: boolean;
  isListEnabled: boolean;
  priority: number | null;
}

// ── Person ────────────────────────────────────────────────────────────

export interface PersonData {
  id: number | null;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  suffix: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  aliases: string | null;
  employeeId: string | null;
  employer: string | null;
  dob: string | null;
  gender: number | null;
  race: number | null;
  height: HeightData | null;
  weight: WeightData | null;
  createdByCompanyId: number | null;
  createdByCompanyName: string | null;
  eyeColor: number | null;
  hairColor: number | null;
  notes: string | null;
  locName: string | null;
  locAddress: string | null;
  locAddress2: string | null;
  locCity: string | null;
  locState: string | null;
  locZip: string | null;
  locCountry: string | null;
  locFormattedAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  customLocationId: boolean | null;
  driversLicenseNum: string | null;
  driversLicenseType: number | null;
  driversLicenseClass: string | null;
  driversLicenseIssued: string | null;
  driversLicenseExpires: string | null;
  driversLicenseState: string | null;
  driversLicenseRestrictions: string | null;
  phoneNumbers: PhoneNumberData[];
  emails: EmailData[];
  descriptionPicId: number | null;
  attachmentList: AttachmentData[];
  connections: ConnectionsData;
  warnings: WarningData[];
  citationCount: number | null;
}

// ── Vehicle ───────────────────────────────────────────────────────────

export interface VehicleData {
  id: number | null;
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  plateState: string | null;
  plateNumber: string | null;
  plateCountry: string | null;
  temporaryPlate: string | null;
  plateIssued: string | null;
  plateExpires: string | null;
  insuranceCompanyName: string | null;
  insuranceExpires: string | null;
  insurancePolicyNumber: string | null;
  insuranceCompanyPhone: string | null;
  vin: string | null;
  bodyStyle: string | null;
  vehicleType: number | null;
  notes: string | null;
  descriptionPicId: number | null;
  attachmentList: AttachmentData[];
  createdByCompanyId: number | null;
  createdByCompanyName: string | null;
  warnings: WarningData[];
  connections: ConnectionsData;
}

// ── Business ──────────────────────────────────────────────────────────

export interface BusinessData {
  id: number | null;
  businessName: string | null;
  branchName: string | null;
  website: string | null;
  businessType: string | null;
  notes: string | null;
  locName: string | null;
  locAddress: string | null;
  locAddress2: string | null;
  locCity: string | null;
  locState: string | null;
  locZip: string | null;
  locCountry: string | null;
  locFormattedAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  customLocationId: boolean | null;
  emails: EmailData[];
  descriptionPicInfoId: number | null;
  attachmentList: AttachmentData[];
  createdByCompanyId: number | null;
  createdByCompanyName: string | null;
  phoneNumbers: PhoneNumberData[];
  warnings: WarningData[];
  connections: ConnectionsData;
}

// ── Client Group ──────────────────────────────────────────────────────

export interface ClientGroupSiteData {
  uuid?: string;
  id: number;
  siteName: string;
  location: string;
}

export interface ClientAutomatedNotificationData {
  id: number | null;
  notifiedName: string | null;
  notifiedEmail: string | null;
  notificationTimeZoneId: string | null;
  notificationTime: number | null;
  activityReportNotificationFrequency: string | null;
}

export interface ClientGroupData {
  id: number | null;
  name: string | null;
  website: string | null;
  notes: string | null;
  locName: string | null;
  locAddress: string | null;
  locAddress2: string | null;
  locCity: string | null;
  locState: string | null;
  locZip: string | null;
  locCountry: string | null;
  locFormattedAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  customLocationId: boolean | null;
  accountNumber: string | null;
  authorizationCode: string | null;
  notificationDefaultTimeZoneId: string | null;
  notificationDefaultTime: number | null;
  emails: EmailData[];
  descriptionPicInfoId: number | null;
  attachmentList: AttachmentData[];
  sites: ClientGroupSiteData[];
  automatedNotificationList: ClientAutomatedNotificationData[];
  phoneNumbers: PhoneNumberData[];
  warnings: WarningData[];
  gateCodes: GateCodeData[];
  connections: ConnectionsData;
}

// ── Case Report ───────────────────────────────────────────────────────

export interface ReportData {
  id: number | null;
  caseId: string;
  lockedFor: number | null;
  authorId: number | null;
  authorTimeZoneId: string | null;
  isSubmitted: boolean | null;
  isApproved: boolean | null;
  callCardId: number | null;
  originalPDF: any;
  isInternalInvestigator: boolean | null;
  reportSubmittedForApproval: boolean | null;
  returnToAuthorComments: string;
  reportApproved: boolean | null;
  reportSubmitted: boolean | null;
  natureOfCall: number[] | null;
  natureOfCallValues: string[] | null;
  incidentDescription: string;
  incidentOccurredFrom: string | null;
  incidentOccurredTo: string | null;
  notifiedOfIncidentVia: number | null;
  notifiedOfIncidentTime: string | null;
  notifiedPerson: number | null;
  notifiedAs: number | null;
  callLocName: string;
  callLocAddress: string;
  callLocAddress2: string;
  callLocCity: string;
  callLocState: string;
  callLocZip: string;
  callLocCountry: string;
  callLocFormattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  customLocationId: boolean | null;
  callDisposition: number | null;
  callDispositionValue: string | null;
  dispositionNotes: string | null;
  primaryOfficer: number | null;
  primaryDispatched: string | null;
  primaryEnroute: string | null;
  primaryOnScene: string | null;
  primaryCleared: string | null;
  backupUnits: any[];
  respondingAgencies: any[];
  offenders: any[];
  accident: any;
  propertyList: any[];
  medicalReports: any[];
  statementList: any[];
  digitalEvidenceList: any[];
  narrative: string;
  narrativeAttachmentInfoList: AttachmentInfoData[];
}

// ── Citation ──────────────────────────────────────────────────────────

export interface CitationData {
  id: number | null;
  citationNumber: string | null;
  status: string;
  voidedReason: string | null;
  voidedBy: number | null;
  voidedByName: string | null;
  voidedDate: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdDate: string | null;
  modifiedBy: number | null;
  modifiedByName: string | null;
  modifiedDate: string | null;
  dateOfIssuance: string | null;
  issuingOfficerId: number | null;
  issuingOfficerName: string | null;
  issuingOfficerBadgeNumber: string | null;
  issuingOfficerAgency: string | null;
  propertyLocation: string | null;
  npsdPersonId: number | null;
  offenderSnapshot: any;
  callCardId: number | null;
  violationTypes: number[];
  otherViolation: string | null;
  offenseDateTime: string | null;
  offenseLocation: string | null;
  allegedSpeed: number | null;
  speedLimit: number | null;
  npsdVehicleId: number | null;
  vehicleSnapshot: any;
  fineAmount: number | null;
  fineToBeDetermined: boolean;
  probableCauseNarrative: string | null;
  bodyCamReference: string | null;
  cadEventNumber: string | null;
  attachmentList: AttachmentData[];
  trespassStatus: any;
  policeCaseNumber: string | null;
  trespassDateTime: string | null;
  signatureAttachmentId: number | null;
  signatureAttachmentActivationKey: string | null;
  signatureUrl: string | null;
  signatureRefused: boolean;
  notes: string | null;
  publicNotes: string | null;
  courtDateTime: string | null;
}

// ── Company ───────────────────────────────────────────────────────────

export interface CompanyData {
  name: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  authorizorName: string;
}

// ── Map Overlay ───────────────────────────────────────────────────────

export interface PolygonOverlayData {
  id: number;
  label: string;
  isPatrolZone: boolean;
  isVisible: boolean;
  polygon: Array<{ lat: number; lng: number }>;
  color: string;
}

export interface MapOverlayGetResult {
  polygonOverlays: PolygonOverlayData[];
}

// ── Paginated result wrapper ──────────────────────────────────────────

export interface PaginatedResult<T = any> {
  data: T[];
  totalCount: number;
}

// ── Enums (mirroring legacy values) ───────────────────────────────────

export enum CallCardInProgressStatus {
  active = 1,
  pending = 2,
  closed = 3,
}

export enum MDTInProgressStatus {
  open = 1,
  active = 2,
  pending = 3,
  closed = 4,
}

export enum CallCardStatus {
  CASE_OPEN = 1,
  CASE_CLOSED = 2,
}

export enum CCAction {
  CLOSE = 1,
  UPDATE = 2,
  ADD = 3,
  RECLOSE = 4,
}

export type NpsdType = 'person' | 'vehicle' | 'business' | 'client';

// ── Custom Forms ──────────────────────────────────────────────────────
// Mirrors backend shapes in com.kerkton.orna.viewModel.form.*
// See docs/house-check-request/implementation-plan.md §2.3.

/** Host record type a schema attaches to. */
export type AttachableRecordType = 'CLIENT' | 'CASE_REPORT';

/**
 * Field type — serialized as lowerCamelCase by backend @JsonValue
 * (e.g. Groovy enum CHECKBOX_GROUP → JSON 'checkboxGroup').
 */
export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'date'
  | 'phone'
  | 'email'
  | 'number'
  | 'radio'
  | 'select'
  | 'checkboxGroup'
  | 'table'
  | 'computed';

export interface FormFieldOptionData {
  value: string;
  label: string;
}

export interface FormFieldShowIfData {
  key: string;
  equals: string | number | boolean | null;
}

/** Per-field schema shape inside sections_json. Matches backend FormField. */
export interface FormFieldData {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  searchable?: boolean;
  summary?: boolean;
  /**
   * Surface this field on the live tour stop tab (when the schema's
   * Independent of `summary` (which drives the tour stop summary panel).
   */
  tab?: boolean;
  options?: FormFieldOptionData[];
  showIf?: FormFieldShowIfData;
  formula?: string;
  columns?: FormFieldData[];
  /** Optional sub-section heading rendered above this field. Empty string renders just the rule (no text). */
  subheading?: string;
  /** Render on the same row as the preceding visible field. */
  inlineWithPrevious?: boolean;
  /** Span both columns of the section grid (full-width). */
  wide?: boolean;
  /** Display formatter (computed/number fields). e.g. 'currency'. */
  format?: string;
  /** Row layout. 'horizontal' puts the label on the left and the value on the right
   * (common for billing/summary cards). Default behavior is stacked. */
  layout?: 'horizontal';
  /** TABLE only — locked rows with predefined first-column labels. */
  predefinedRows?: { id: string; label: string }[];
  [extra: string]: unknown;
}

export interface FormSectionData {
  id: string;
  title: string;
  fields: FormFieldData[];
  /** Render in the right-side column on wider screens. */
  sidebar?: boolean;
}

/**
 * Lean projection used by Records Search to render per-schema filter sections.
 * Returned by GET /admin/form/schema/searchable-fields. Only includes the bits
 * the search UI needs — never formulas, showIf, layout flags, etc.
 */
export interface FormSearchableFieldData {
  key: string;
  label: string;
  type: FormFieldType;
  /** Populated for radio/select. Each entry is `{value, label}`. */
  options?: FormFieldOptionData[];
}

export interface FormSchemaSearchableFieldsData {
  schemaId: number;
  schemaName: string;
  fields: FormSearchableFieldData[];
}

export interface FormSchemaData {
  id: number;
  name: string;
  attachTo: AttachableRecordType | string;
  currentVersionNumber: number | null;
  isActive: boolean;
  updatedAt: string | null;
  currentVersion?: FormSchemaVersionDetailData;
}

export interface FormSchemaVersionDetailData {
  id: number;
  versionNumber: number;
  dateCreated: string;
  createdById: number;
  sections: FormSectionData[];
}

/** Codes are serialized with hyphens by backend @JsonValue. */
export type FormSchemaWarningCode =
  | 'W-REMOVE'
  | 'W-RENAME'
  | 'W-SEARCHABLE-REMOVED'
  | 'W-SEARCHABLE-CHANGED'
  | 'W-SUMMARY-REMOVED'
  | 'W-TAB-REMOVED'
  | 'W-ACTIVE-WINDOW-ADDED'
  | 'W-REINDEX-TYPE-MISMATCH'
  | 'W-SCHEMA-VERSION-STALE';

export interface FormSchemaWarningData {
  code: FormSchemaWarningCode;
  message: string;
  fieldKey?: string | null;
  affectedInstanceCount?: number | null;
  extra?: Record<string, unknown> | null;
}

export interface FormSchemaSaveResultData {
  detail: FormSchemaData;
  warnings: FormSchemaWarningData[];
}

export interface FormSchemaDeleteResultData {
  affectedInstanceCount: number;
}

export interface FormSchemaValidationResultData {
  errors: string[];
  warnings: FormSchemaWarningData[];
  /**
   * Predictive sidecar-reindex impact for this proposed update. Null when the schema has
   * no current version yet (pure create-shaped preview); otherwise always populated, with
   * willReindex=false meaning the change does not affect the searchable set.
   */
  reindexImpact?: FormReindexImpactData | null;
}

/** Mirrors backend FormReindexImpact viewmodel; surfaced via /preview-update. */
export interface FormReindexImpactData {
  willReindex: boolean;
  affectedInstanceCount: number;
  cap: number;
  capExceeded: boolean;
  /** Rough heuristic; bucket into "a few seconds" / "about 30 seconds" / "over a minute" rather than render literally. */
  estimatedSeconds: number;
}

/**
 * Mirrors backend ToggleSearchableCommand. Body for the per-field searchable toggle
 * endpoints (POST /field/{key}/searchable/preview and PUT /field/{key}/searchable).
 * Searchable changes on existing fields are rejected through the regular update path —
 * this command targets one field at a time so the frontend can show a deliberate
 * confirmation UX with reindex impact preview.
 */
export interface ToggleSearchableCommandData {
  searchable: boolean;
}

export interface FormReindexResultData {
  instancesProcessed: number;
  durationMs: number;
  typeMismatchCount: number;
}

export interface FormInstanceData {
  id: number;
  schemaId: number;
  recordType: AttachableRecordType | string;
  recordId: number;
  values: Record<string, unknown>;
  updatedById: number | null;
  updatedAt: string | null;
}

/** Response envelope from POST /upsert. */
export interface FormInstanceUpsertResultData {
  instanceView: FormInstanceData;
  isStaleVersion: boolean;
}

export interface FormSummaryFieldData {
  key: string;
  label: string;
  value: unknown;
  type: FormFieldType;
  /** Pre-formatted display string from the backend (option labels resolved,
   * dates formatted MM/DD/YYYY, currency formatted, lists joined). */
  displayValue?: string;
  /** Section the field belongs to, for grouping in the UI. */
  sectionId?: string;
  sectionTitle?: string;
}

/** Tour-stop projection row (§2.6). */
export interface FormSummaryData {
  schemaId: number;
  schemaName: string;
  instanceId: number;
  fields: FormSummaryFieldData[];
}

/**
 * One label/value row inside a tour stop form tab. Same display semantics as
 * FormSummaryFieldData (pre-formatted `displayValue`) but distinct so the
 * summary and tab surfaces can evolve independently.
 */
export interface FormTabFieldData {
  key: string;
  label: string;
  value: unknown;
  type: FormFieldType;
  displayValue?: string;
  sectionId?: string;
  sectionTitle?: string;
}

/**
 * One extra tab on the live tour stop detail page, sourced from a form
 * instance whose schema has at least one field with `tab: true`. Returned by
 * GET /tourstop/{id}/form-tabs. The tab strip on TourStopDetail renders one
 * button per entry, with `tabTitle` as the label.
 *
 * `tabTitle` is currently always the schema name. The field is kept on the
 * wire so a future per-schema label override can land without clients changing.
 */
export interface FormTabData {
  schemaId: number;
  tabTitle: string;
  instanceId: number;
  fields: FormTabFieldData[];
}

/** Search surface types (§5). */
export type FormSearchOp = 'EQ' | 'NEQ' | 'GT' | 'GTE' | 'LT' | 'LTE' | 'CONTAINS' | 'BETWEEN';

export interface FormSearchPredicateData {
  fieldKey: string;
  op: FormSearchOp;
  value?: unknown;
  value2?: unknown;
}

export interface FormSearchRequestData {
  schemaId: number;
  attachTo: AttachableRecordType | string;
  predicates: FormSearchPredicateData[];
  page?: number;
  pageSize?: number;
}

export interface FormHostDisplayData {
  recordType: AttachableRecordType | string;
  recordId: number;
  displayName: string | null;
  displayAddress: string | null;
}

export interface FormSearchResultData {
  instance: FormInstanceData;
  host: FormHostDisplayData | null;
  summaryFields: FormSummaryFieldData[];
}

export interface FormSearchResultPageData {
  total: number;
  page: number;
  pageSize: number;
  results: FormSearchResultData[];
}

/** Request-body shapes (POSTed to the server). */
export interface CreateFormSchemaCommandData {
  name: string;
  attachTo: AttachableRecordType | string;
  sections: FormSectionData[];
}

export interface UpdateFormSchemaCommandData extends CreateFormSchemaCommandData {}

export interface UpdateFormSchemaMetaCommandData {
  name?: string | null;
  isActive?: boolean | null;
}

export interface UpsertFormInstanceCommandData {
  id?: number | null;
  /** Required on create; ignored on update (the existing instance's schema is reused). */
  formSchemaId?: number | null;
  recordType: AttachableRecordType | string;
  recordId: number;
  values: Record<string, unknown>;
}
